import React, { createRef, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { medium, colors, pxToRem, mediumInter } from '@/styles';
import { ClickableElement } from '@/atoms';
import { MagnifyingGlass, Close } from '@/svgs';

/**
 * SearchBar component
 */

/**
 * @typedef {'small'|'medium'|'large'} Size
 */

/**
 * Search Icon Size Mapping
 * @type {Record<Size, number>}
 */
const searchIconSizes = {
  // Divide the designed size by 2.85 to "undo" multiplication in `MagnifyingGlass` component
  small: 12.31 / 2.85,
  medium: 14.36 / 2.85,
  large: 14.36 / 2.85,
};

/**
 * Search Icon Size Mapping
 * @type {Record<Size, number>}
 */
const clearIconSizes = {
  // Divide the designed size by 2.85 to "undo" multiplication in `Close` component
  small: 10 / 2.85,
  medium: 12 / 2.85,
  large: 12 / 2.85,
};

/**
 * @param {import('./SearchBar').SearchBarProps} props
 */
const SearchBar = ({
  className,
  data,
  onChange,
  onSelect,
  onFocus,
  size,
  isRebrand,
  placeHolder,
  id,
}) => {
  const inputRef = createRef();
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showOptions, setShowOptions] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [activeOption, setActiveOption] = useState(null);

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  const handleChange = (event) => {
    const input = event.currentTarget.value;

    const filtered = data.filter(
      (optionName) => optionName.toLowerCase().indexOf(input.toLowerCase()) > -1,
    );

    setActiveOption(-1);
    setFilteredOptions(filtered);
    setShowOptions(true);
    setUserInput(input);
    onChange(input);
  };

  /**
   * @type {(event: React.MouseEvent<HTMLElement>) => void}
   */
  const onClickOption = useCallback(
    (event) => {
      const input = event.currentTarget.innerText;
      setActiveOption(0);
      setFilteredOptions([]);
      setShowOptions(false);
      setUserInput(input);

      onSelect(input);
    },
    [onSelect],
  );

  /**
   * @type {(event: React.KeyboardEvent) => void}
   */
  const onKeyDown = useCallback(
    (event) => {
      const input = data.length && activeOption >= 0 ? filteredOptions[activeOption] : userInput;

      switch (event.key) {
        // Return
        case 'Enter':
          setActiveOption(0);
          setShowOptions(false);
          setUserInput(input);

          onSelect(input);
          break;
        // Arrow up
        case 'ArrowUp':
          if (activeOption === -1) {
            return;
          }
          setActiveOption(activeOption - 1);
          break;
        // Arrow down
        case 'ArrowDown':
          if (activeOption === filteredOptions.length - 1) {
            return;
          }
          setActiveOption(activeOption + 1);
          break;
      }
    },
    [activeOption, filteredOptions, userInput, data, onSelect],
  );

  const onClear = () => {
    setUserInput('');
    onSelect('');
  };

  const getFilteredOptions = useCallback(
    () => (
      <ul
        role="listbox"
        className="options"
        data-testid="searchbar-options"
        id={`${id}-suggestions`}
      >
        {filteredOptions.map((optionName, index) => (
          <li
            role="option"
            aria-selected={index === activeOption}
            className={index === activeOption ? 'option-active' : ''}
            key={optionName}
          >
            <ClickableElement onClick={onClickOption}>{optionName}</ClickableElement>
          </li>
        ))}
      </ul>
    ),
    [activeOption, filteredOptions, id, onClickOption],
  );

  const hasSuggestions = data.length !== 0;

  return (
    <div className={`${className} ${isRebrand ? 'brand--new' : ''}`}>
      <div className="search">
        <input
          type="text"
          role={hasSuggestions ? 'combobox' : 'searchbox'}
          className="search-box"
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          placeholder={placeHolder}
          value={userInput}
          ref={inputRef}
          aria-controls={hasSuggestions ? `${id}-suggestions` : ''}
          aria-autocomplete={hasSuggestions ? 'list' : 'none'}
          aria-haspopup={hasSuggestions ? 'listbox' : 'false'}
          aria-expanded={Boolean(showOptions && userInput)}
        />
        {userInput && (
          <ClickableElement onClick={onClear} className="clear-btn">
            <Close size={`${clearIconSizes[size]}`} fill={colors.boulder} />
          </ClickableElement>
        )}
        {!userInput && !isRebrand && (
          // original brand should show the search button when there is no user input
          <ClickableElement onClick={() => onSelect(userInput)} className="search-btn">
            <MagnifyingGlass size={`${searchIconSizes[size]}`} fill={colors.silverSandAlt} />
          </ClickableElement>
        )}
        {isRebrand && (
          // new brand does not have a search button, but an icon that appears first
          // clicking the icon will focus the input
          <ClickableElement
            tabIndex={-1}
            onClick={() => inputRef.current?.focus()}
            className="icon--search"
          >
            <MagnifyingGlass title="Search" fill={colors.shadow} />
          </ClickableElement>
        )}
      </div>
      {Boolean(showOptions && userInput) && getFilteredOptions()}
    </div>
  );
};

SearchBar.propTypes = {
  /** Emotion styles */
  className: PropTypes.string.isRequired,
  /** What to do when the search field's value changes */
  onChange: PropTypes.func,
  /** What to do when a value is selected or the return key is hit */
  onSelect: PropTypes.func.isRequired,
  /** Optional data for auto complete */
  data: PropTypes.arrayOf(PropTypes.string),
  /** Size of the component (Options: `small`, `medium`, `large`) */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Use focused on the search bar */
  onFocus: PropTypes.func,
  /** Use new branding */
  isRebrand: PropTypes.bool,
  /** Search box placeholder */
  placeHolder: PropTypes.string,
  /** Search box ID */
  id: PropTypes.string,
};

SearchBar.defaultProps = {
  onChange: () => {},
  onFocus: () => {},
  data: [],
  size: 'medium',
  isRebrand: false,
  placeHolder: 'Search',
  id: 'searchbar',
};

/**
 * @param {{ size: Size }} props
 */
function getFontSize({ size }) {
  if (size === 'small') {
    return `${pxToRem(13)}rem`;
  }
  if (size === 'large') {
    return `${pxToRem(16)}rem`;
  }
  return `${pxToRem(14)}rem`;
}

/**
 * @param {{ size: Size }} props
 */
function generateSearchBoxStyles({ size }) {
  // medium as default values
  let height = '2rem';
  let paddingLeft = '1.4rem';
  let paddingRight = '2.2rem';

  switch (size) {
    case 'small':
      height = '1.8125rem';
      paddingLeft = '1.2rem';
      break;
    case 'large':
      height = '2.1875rem';
      paddingLeft = '1.6rem';
      paddingRight = '2.3rem';
      break;
  }

  return `
    height: ${height};
    font-size: ${getFontSize({ size })};
    padding-left: ${paddingLeft};
    padding-right: ${paddingRight};
  `;
}

/**
 * @param {{ size: Size }} props
 */
function generateCloseButtonStyles({ size }) {
  // medium as default value
  let bottom = '0.1rem';
  switch (size) {
    case 'small':
      bottom = '0.05rem';
      break;
    case 'large':
      bottom = '0.15rem';
      break;
  }

  return `
    bottom: ${bottom};
  `;
}

/**
 * @param {{ size: Size }} props
 */
function generateSearchButtonStyles({ size }) {
  // medium as default values
  let bottom = '0.05rem';
  switch (size) {
    case 'small':
      bottom = '0';
      break;
    case 'large':
      bottom = '0.1rem';
      break;
  }

  return `
    bottom: ${bottom};
  `;
}

const styledSearchBar = styled(SearchBar)`
  --search-font-size: ${(props) => getFontSize(props)};
  * {
    font-size: var(--search-font-size);
    ${medium()}
  }

  .search {
    background: ${colors.white};
    border-radius: 2.5rem;
    position: relative;

    .search-box {
      border: 0.0625rem solid ${colors.silverSand};
      border-radius: 2.5rem;
      color: ${colors.boulder};
      transition: width 0.3s;
      width: 100%;

      ${generateSearchBoxStyles}

      &:focus {
        border: 0.0625rem solid ${colors.silverSandAlt};
        outline: none;
      }

      ::placeholder {
        color: ${colors.silverSandAlt};
        opacity: 1;
      }
    }

    .search-btn {
      right: 0.95rem;
      ${generateSearchButtonStyles}
    }

    .clear-btn {
      right: 1rem;
      ${generateCloseButtonStyles}
      svg path {
        fill: ${colors.silverSandAlt}; /* fill attribute above does not seem to work */
      }
    }

    .search-btn,
    .clear-btn {
      padding: 0.25rem;
      position: absolute;
      transition: opacity 0.5s;

      &:hover {
        cursor: pointer;

        svg path {
          fill: ${colors.boulder};
        }
      }

      &:focus {
        opacity: 0.6;
      }
    }
  }

  .options {
    transition: 0.3s;
    position: absolute;
    margin: 0;
    padding: 0;
    text-align: left;
    box-shadow: 0 0.375rem 0.375rem 0 ${colors.boxShadow};
    z-index: 2;

    li {
      display: block;
      background: white;
      padding: 0.5rem;
      color: ${colors.boulder};
      cursor: pointer;

      &.option-active {
        background-color: ${colors.fog};
      }

      &:hover {
        background: ${colors.fog};
      }
    }
  }

  &.brand--new {
    * {
      ${mediumInter()};
      font-size: var(--search-font-size);
    }

    .search {
      display: grid;
      grid-template-columns: 30px auto 30px;
      grid-template-areas: 'icon search button';
      align-items: center;
    }

    .search-box {
      grid-column-start: icon;
      grid-column-end: button;
      grid-row: 1;
      border: 2px solid ${colors.whiteSmoke};
      border-radius: 3px;
      ${mediumInter()};
      padding-left: 34px;

      &:focus {
        border: 2px solid ${colors.silverSandAlt};
      }

      ::placeholder {
        color: ${colors.disableGrey};
      }
    }

    .icon--search {
      grid-area: icon;
      padding-top: 4px;
      width: min-content;
      height: min-content;
      cursor: text;
      justify-self: end;
      svg {
        height: calc(var(--search-font-size) - 0.1rem);
      }
    }

    .clear-btn {
      position: relative;
      grid-area: button;
      right: unset;
      bottom: unset;
    }
  }
`;

export default styledSearchBar;
